import {cloneDeep} from 'lodash';
import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {ClinicRegisterDTO} from '../../dtos/clinic';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse, ServiceLoopbackFilters} from '../../services/base-service/base.service';
import {IClinic} from './clinic.interface';
import {UploadFileService} from '../../services/file-service/upload-file.service';
import {IClinicalStatistic} from './clinical-statistic.interface';
import {IClinicalObjective} from './clinical-objective.interface';
import {firstValueFrom} from 'rxjs';
import {ExpertAction, IExpert} from '../expert';
import {IPatient, PatientAction} from '../patient';

export interface IRClinic {
  clinics: IClinic[];
  selectedClinic: string;
  statistics: IClinicalStatistic;
  objectives: IClinicalObjective[];
  alreadyAcceptTerms?: { acceptedTerms: boolean };
}

export namespace ClinicActions {
  export class GetClinicBasicInformationAction {
    static readonly type: string = '[Clinic] Get Clinic Basic Information Action';
    constructor(public id: string) { }
  }

  export class Create {
    static readonly type: string = '[Clinic] Create Clinic Action';
    constructor(public clinic: ClinicRegisterDTO, public file: File) { }
  }

  export class Get {
    static readonly type = '[Clinic] Get Clinic';
    constructor(public clinicId: string, public filters?: ServiceLoopbackFilters) { }
  }

  export class Update {
    static readonly type: string = '[Clinic] Update Clinic';
    constructor(public clinicId: string, public clinic: Partial<IClinic>) { }
  }

  export class GetExperts {
    static readonly type: string = '[Clinic] Get Experts By Clinic Id Action';
    constructor(
      public clinicId: string,
      public search: string = '',
      public filters?: {
        params: {
          skip: number,
          limit: number,
          onlyAvailable?: boolean
        }
      },
      public available?: boolean
    ) { }
  }

  export class GetExpertsByBranch {
    static readonly type: string = '[Clinic] Get Experts By Branch Id Action';
    constructor(
      public clinicId: string,
      public clinicBranchId?: string,
      public search: string = '',
      public filters?: {
        params: {
          skip: number,
          limit: number
        }
      }
    ) { }
  }

  export class GetPatients {
    static readonly type: string = '[Clinic] Get Patients By ClinicId Action';
    constructor(
      public clinicId: string,
      public search: string = '',
      public status: string,
      public filters?: {
        params: {
          skip: number,
          limit: number
        }
      }
    ) { }
  }

  export class GetPatientAssistants {
    static readonly type: string = '[Clinic] Get Patient Assistants By ClinicId Action';
    constructor(public clinicId: string, public search: string = '', public filters?: { params: { skip: number, limit: number } }) { }
  }

  export class GetStatistics {
    static readonly type: string = '[Clinic] Get Clinic Statistics Action';
    constructor(public clinicId: string, public filters?: { params: { year: number, month: number, timeZone: string } }) { }
  }

  export class AlreadyAcceptTerms {
    static readonly type: string = '[Clinic] Already Accept Terms Action';
    constructor(public clinicId: string) { }
  }

  export class UpdateCancellationPromo {
    static readonly type: string = '[Clinic] Update Cancellation Promo Action';
    constructor(public value: boolean) { }
  }

  export class GetObjectives {
    static readonly type: string = '[Clinic] Get Clinic Objectives Action';
    constructor() { }
  }
}

@State<IRClinic>({
  name: 'ClinicState',
  defaults: {
    clinics: [],
    selectedClinic: null,
    statistics: null,
    objectives: []
  },
})

@Injectable()
export class ClinicState {
  constructor(
    private baseService: BaseService<any>,
    private store: Store,
    private uploadFileService: UploadFileService
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getClinicSelected(state: IRClinic): IClinic {
    return state?.clinics.find((clinic: IClinic) => clinic._id === state.selectedClinic);
  }

  @Selector()
  public static getClinics(state: IRClinic): IClinic[] {
    return state?.clinics ?? [];
  }

  @Selector()
  public static getStatistics(state: IRClinic): IClinicalStatistic {
    return state.statistics;
  }

  @Selector()
  public static getObjectives(state: IRClinic): IClinicalObjective[] {
    return state.objectives;
  }

  @Selector()
  public static getAlreadyAcceptTerms(state: IRClinic): { acceptedTerms: boolean } {
    return state.alreadyAcceptTerms;
  }

  @Selector()
  public static getClinicalOwnerId(state: IRClinic): string {
    const clinic = state?.clinics.find(item => item._id === state.selectedClinic);
    if (clinic) {
      return clinic.ownerId;
    }
    return;
  }

  @Action(ClinicActions.GetClinicBasicInformationAction)
  async getClinicBasicInformationAction(ctx: StateContext<IRClinic>, action?: ClinicActions.GetClinicBasicInformationAction): Promise<void> {
    const response: DataBaseServiceResponse<any> = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.id}/basic-information`, void 0)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      clinics: [response.entity],
      selectedClinic: response.entity._id
    });
  }

  @Action(ClinicActions.Create)
  async createClinicAction(ctx: StateContext<IRClinic>, action: ClinicActions.Create): Promise<void> {
    const response: DataBaseServiceResponse<IClinic> = await firstValueFrom((this.baseService.post(`${this.url}/clinics`, action.clinic)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      clinics: [response.entity],
      selectedClinic: response.entity._id
    });
    if (action.file) {
      await this.uploadFileService.postFile(action.file, `clinics/${response.entity._id}/upload-logo`, ['png', 'jpg', 'jpeg']);
    }
  }

  @Action(ClinicActions.Get)
  async getClinic(
    ctx: StateContext<IRClinic>,
    action: ClinicActions.Get
  ): Promise<void> {
    action.filters = {
      ...action.filters
    };
    const response: DataBaseServiceResponse<IClinic> = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.clinicId}`, action.filters)));

    if (!response.success) return;

    const clinics = cloneDeep(ctx.getState()?.clinics || []);
    const index = clinics.findIndex((clinic: IClinic) => clinic._id === action.clinicId);
    if (index !== -1) {
      clinics[index] = response.entity;
    } else {
      clinics.push(response.entity);
    }

    ctx.patchState({
      clinics,
      selectedClinic: action.clinicId,
    });
  }

  @Action(ClinicActions.Update)
  async patchClinic(
    ctx: StateContext<IRClinic>,
    action: ClinicActions.Update
  ): Promise<void> {
    const response: DataBaseServiceResponse<IClinic> = await firstValueFrom((this.baseService.patch(`${this.url}/clinics/${action.clinicId}`, action.clinic)));
    if (response.error) throw new Error(response.message);
  }

  @Action(ClinicActions.GetExperts)
  async getExpertsByClinicIdAction(ctx: StateContext<IRClinic>, action: ClinicActions.GetExperts): Promise<void> {
    const stringUrl = `${this.url}/clinics/${action.clinicId}/experts?search=${action.search}`;
    let filters;
    if (action.available) {
      action.filters.params.onlyAvailable = true;
    } else if (action.available && !action.filters) {
      filters = {
        params: {
          available: true
        }
      };
    }
    const response: DataBaseServiceResponse<IExpert[]> = await firstValueFrom(
      this.baseService.get(stringUrl, void 0, void 0, action.filters ?? filters)
    );
    if (response.error) throw new Error(response.message);
    await firstValueFrom((this.store.dispatch(new ExpertAction.SetExperts(response.entity))));
  }

  @Action(ClinicActions.GetExpertsByBranch)
  async getExpertsByClinicAndBranchAction(ctx: StateContext<IRClinic>, action: ClinicActions.GetExpertsByBranch): Promise<void> {
    const stringUrl = `${this.url}/clinics/${action.clinicId}/branches/${action.clinicBranchId}/experts?search=${action.search}`;

    const response: DataBaseServiceResponse<IExpert[]> = await firstValueFrom(
      this.baseService.get(stringUrl, void 0, void 0, action.filters)
    );
    if (response.error) throw new Error(response.message);
    await firstValueFrom((this.store.dispatch(new ExpertAction.SetExperts(response.entity))));
  }

  @Action(ClinicActions.GetPatients)
  async getPatientsByClinicIdAction(ctx: StateContext<IRClinic>, action: ClinicActions.GetPatients): Promise<void> {
    const response: DataBaseServiceResponse<IPatient[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/clinics/${action.clinicId}/patients`, void 0, void 0, {
      params: {
        ...action.filters?.params ?? {},
        search: action.search,
        status: action.status
      }
    }));
    if (response.error) throw new Error(response.message);
    await firstValueFrom((this.store.dispatch(new PatientAction.SetPatients(response.entity))));
  }

  @Action(ClinicActions.GetPatientAssistants)
  async getPatientAssistantsByClinicIdAction(ctx: StateContext<IRClinic>, action: ClinicActions.GetPatientAssistants): Promise<void> {
    const response: DataBaseServiceResponse<IPatient[]> = await firstValueFrom(
      this.baseService.get(`${this.url}/clinics/${action.clinicId}/patients`, void 0, void 0, {
      params: {
        ...action.filters?.params,
        search: action.search,
      }
    }));
    if (response.error) throw new Error(response.message);
    await firstValueFrom((this.store.dispatch(new PatientAction.SetAssistants(response.entity))));
  }

  @Action(ClinicActions.GetStatistics)
  async getClinicStatisticsAction(ctx: StateContext<IRClinic>, { clinicId, filters }: ClinicActions.GetStatistics): Promise<void> {
    const response: DataBaseServiceResponse<IClinicalStatistic> = await firstValueFrom(
      this.baseService.get(`${this.url}/statistics/${clinicId}/dashboard`, void 0, void 0, {
        params: {
          ...filters.params
        }
      }));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      statistics: response.entity
    });
  }

  @Action(ClinicActions.AlreadyAcceptTerms)
  async alreadyAcceptTermsAction(ctx: StateContext<IRClinic>, action: ClinicActions.AlreadyAcceptTerms): Promise<void> {
    const response: DataBaseServiceResponse<{ acceptedTerms: boolean }> = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.clinicId}/accept-terms`)));
    if (response.error) throw new Error(response.message);
    ctx.patchState({
      alreadyAcceptTerms: response.entity
    });
  }

  @Action(ClinicActions.GetObjectives)
  async getClinicObjectivesAction(ctx: StateContext<IRClinic>, action: ClinicActions.GetObjectives): Promise<void> {
    if (!ctx.getState().objectives || ctx.getState().objectives.length === 0) {
      const response = await firstValueFrom((this.baseService.get(`${this.url}/clinics/objective/global`, void 0, void 0)));
      if (response.error) throw new Error(response.message);
      ctx.patchState({
        objectives: response.entity
      });
    }
  }

  @Action(ClinicActions.UpdateCancellationPromo)
  async updateCancellationPromoAction(ctx: StateContext<IRClinic>, action: ClinicActions.UpdateCancellationPromo): Promise<void> {
    const clinicIndex = ctx.getState() ? ctx.getState().clinics.findIndex(item => item._id === ctx.getState().selectedClinic) : -1;
    const clinics: IClinic[] = ctx.getState() ? cloneDeep(ctx.getState().clinics) : [];
    if (clinics.length === 0) {
      return;
    }
    if (clinicIndex !== -1) {
      clinics[clinicIndex].metadata.cancellationPromo = action.value;
      ctx.patchState({
        clinics
      });
    }
  }
}
